import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useLanguage } from "../../hooks/useLanguage"
import { Language } from "../../lib/translations"
import { LogoLockup } from "../Logo"
import { Close, Menu } from "../icons"

const languages: { code: Language; label: string; path: string; hrefLang: string }[] = [
  { code: "pt-BR", label: "PT", path: "/", hrefLang: "pt-BR" },
  { code: "en", label: "EN", path: "/en", hrefLang: "en" },
]

export function LanguageSwitch({ className = "" }: { className?: string }) {
  const { language, t } = useLanguage()
  return (
    <div role="group" aria-label={t.nav.language} className={`inline-flex items-center font-mono text-[13px] ${className}`}>
      {languages.map(({ code, label, path, hrefLang }) => {
        const active = code === language
        return (
          <Link
            key={code}
            to={path}
            replace
            hrefLang={hrefLang}
            lang={hrefLang}
            aria-current={active ? "true" : undefined}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full transition-colors ${
              active ? "bg-gms-mist font-medium text-gms-ink" : "text-gms-subtle hover:text-gms-ink"
            }`}
          >
            {label}
          </Link>
        )
      })}
    </div>
  )
}

// Desktop: um link só, para a outra língua (o par PT/EN completo fica no menu mobile)
function LanguageToggle({ className = "" }: { className?: string }) {
  const { language, t } = useLanguage()
  const target = languages.find((l) => l.code !== language)!
  return (
    <Link
      to={target.path}
      replace
      hrefLang={target.hrefLang}
      lang={target.hrefLang}
      aria-label={`${t.nav.language}: ${target.code === "en" ? "English" : "Português"}`}
      className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-gms-line-2 font-mono text-[13px] text-gms-ink transition-colors hover:bg-gms-mist ${className}`}
    >
      {target.label}
    </Link>
  )
}

export default function Navbar() {
  const { language, t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const homePath = language === "en" ? "/en" : "/"

  const navItems = [
    { label: t.nav.services, href: "#servicos" },
    { label: t.nav.process, href: "#processo" },
    { label: t.nav.about, href: "#sobre" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.blog, href: "/blog" },
  ]

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setIsOpen(false)
    const mq = window.matchMedia("(min-width: 761px)")
    const onWide = () => mq.matches && setIsOpen(false)
    window.addEventListener("keydown", onKey)
    mq.addEventListener("change", onWide)
    return () => {
      window.removeEventListener("keydown", onKey)
      mq.removeEventListener("change", onWide)
    }
  }, [isOpen])

  useEffect(() => setIsOpen(false), [language])

  const close = () => setIsOpen(false)

  return (
    <div className="sticky top-0 z-30 border-b border-[rgba(228,228,231,0.8)] bg-[rgba(255,255,255,0.82)] [-webkit-backdrop-filter:saturate(1.6)_blur(14px)] [backdrop-filter:saturate(1.6)_blur(14px)]">
      <header className="mx-auto flex max-w-gms items-center justify-between gap-x-8 gap-y-4 px-gutter py-[14px] max-[480px]:gap-x-2">
        <Link
          to={homePath}
          onClick={() => window.scrollTo({ top: 0 })}
          aria-label="GMS Tech"
          className="inline-flex min-h-[44px] items-center gap-[10px] text-gms-ink no-underline"
        >
          <LogoLockup size={34} textSize={22} blink />
        </Link>

        <nav aria-label={t.nav.label} className="flex flex-wrap gap-x-8 gap-y-[6px] text-[15.5px] font-medium max-[760px]:hidden">
          {navItems.map(({ label, href }) => (
            <a key={href} href={href} className="gms-nav-link inline-flex items-center [@media(pointer:coarse)]:min-h-[44px]">
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 max-[360px]:gap-1">
          <LanguageToggle className="max-[760px]:hidden" />
          <a
            href="#contato"
            onClick={close}
            className="gms-btn inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full bg-gms-ink px-5 text-[15px] max-[360px]:px-[14px] font-semibold text-white no-underline hover:text-white"
          >
            <span className="max-[480px]:hidden">{t.nav.cta}</span>
            <span className="hidden max-[480px]:inline">{t.nav.ctaShort}</span>
          </a>
          <button
            type="button"
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            aria-controls="gms-mobile-menu"
            aria-label={isOpen ? t.nav.closeMenu : t.nav.openMenu}
            className="-mr-[10px] hidden h-11 w-11 items-center justify-center rounded-full text-gms-ink transition-colors hover:bg-gms-mist max-[760px]:inline-flex"
          >
            {isOpen ? <Close size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {isOpen && (
        <div id="gms-mobile-menu" className="absolute left-0 right-0 top-full min-[761px]:hidden">
          <div className="absolute left-0 right-0 top-0 h-[100dvh] bg-[rgba(11,13,18,0.2)]" onClick={close} aria-hidden="true" />
          <nav aria-label={t.nav.label} className="relative border-b border-gms-line bg-white shadow-[0_24px_48px_-28px_rgba(11,13,18,0.35)]">
            <div className="flex flex-col px-gutter pb-5 pt-2">
              {navItems.map(({ label, href }) => (
                <a
                  key={href}
                  href={href}
                  onClick={close}
                  className="flex min-h-[52px] items-center border-b border-gms-line text-[17px] font-medium text-gms-ink no-underline"
                >
                  {label}
                </a>
              ))}
              <div className="flex items-center justify-between gap-4 pt-4">
                <span className="font-mono text-[12.5px] text-gms-subtle">{t.nav.language}</span>
                <LanguageSwitch />
              </div>
            </div>
          </nav>
        </div>
      )}
    </div>
  )
}
